import type { MappedReportData, RelatedFieldObject, ReportSignature } from 'common/models';
import LabeledText from 'components/LabeledText/LabeledText';
import { interpolateRelatedFieldTemplate } from 'helpers/customDataHelper';
import type { ServiceObjectInCustomer } from 'pages/serviceObjects/models';
import React from 'react';

import CustomDataBlock from './CustomDataBlock';
import CustomDataLabeledText from './CustomDataLabeledText';
import CustomDataText from './CustomDataText';
import ServiceObjectData from './ServiceObject/ServiceObjectData';

interface OwnProps {
  section: MappedReportData;
  object: RelatedFieldObject;
  serviceObject?: ServiceObjectInCustomer;
  customFormId: string;
  reportId: string;
  signature?: ReportSignature;
  perServiceObject?: boolean;
  reportTitle: string;
  totalCost: number;
  serviceObjects: ServiceObjectInCustomer[];
  isPublic?: boolean;
  customFormCodeNumber: number;
}

const CustomDataReportSection = (props: OwnProps) => {
  const {
    section,
    object,
    serviceObject,
    customFormId,
    reportId,
    signature,
    perServiceObject,
    reportTitle,
    totalCost,
    serviceObjects,
    isPublic,
    customFormCodeNumber
  } = props;

  const columnsWidthArray = section.width ? section.width.split('/') : undefined;

  const renderSectionItem = (mappedData: MappedReportData, i: number) => {
    if (mappedData.type === 'Section') {
      return (
        <CustomDataReportSection
          key={`${mappedData.type}-${i}`}
          section={mappedData}
          customFormId={customFormId}
          object={object}
          reportId={reportId}
          reportTitle={reportTitle}
          serviceObjects={serviceObjects}
          totalCost={totalCost}
          perServiceObject={perServiceObject}
          serviceObject={serviceObject}
          signature={signature}
          customFormCodeNumber={customFormCodeNumber}
        />
      );
    }

    if (mappedData.type === 'ServiceObjectSection') {
      const filteredServiceObjects = mappedData.serviceObjectCategories
        ? serviceObjects.filter((x) => mappedData.serviceObjectCategories?.includes(x.categorySlug))
        : serviceObjects || [];

      if (mappedData.serviceObjectCategories && filteredServiceObjects.length === 0) {
        return <div key={`${mappedData.type}-${i}`} />;
      }

      return (
        <ServiceObjectData
          key={`${mappedData.type}-${i}`}
          data={mappedData.serviceObjectData!}
          title={mappedData.title}
          displayMode={mappedData.displayMode!}
          serviceObjects={filteredServiceObjects}
          columns={mappedData.columns}
          width={mappedData.width}
        />
      );
    }

    if (mappedData.type === 'FormField') {
      return (
        <CustomDataLabeledText
          key={mappedData.fieldData?.id}
          customData={mappedData.fieldData!}
          totalCost={totalCost}
          currencyId={object.company?.currencyId || ''}
        />
      );
    }

    if (mappedData.type === 'Empty') {
      return <div key={i} className="Empty" />;
    }

    if (mappedData.type === 'CustomBlock') {
      return (
        <CustomDataBlock
          key={`${mappedData.blockProps?.blockType}-${i}`}
          blockProps={mappedData.blockProps!}
          blockData={mappedData.blockData}
          object={object}
          serviceObjectId={serviceObject?.id}
          customFormId={customFormId}
          reportId={reportId}
          signature={signature}
          perServiceObject={perServiceObject}
          reportTitle={reportTitle}
          totalCost={totalCost}
          serviceObjects={serviceObjects}
          isPublic={isPublic}
          customFormCodeNumber={customFormCodeNumber}
        />
      );
    }

    if (mappedData.type === 'TextField') {
      return <CustomDataText key={mappedData.textValue} text={mappedData.textValue!} />;
    }

    const relatedFieldValue =
      mappedData.properties?.template &&
      interpolateRelatedFieldTemplate(mappedData.properties?.template, object, serviceObject);

    return (
      <LabeledText
        label={mappedData.properties?.label || ''}
        value={relatedFieldValue}
        key={mappedData.properties?.template}
      />
    );
  };

  return (
    <div className={`ReportSection ${section.enablePageBreak ? 'enablePageBreak' : ''}`}>
      {(section.showEmptyTitle || section.title) && (
        <div className={`SectionTitle ${section.showEmptyTitle && 'emptyTitle'}`}>
          {section.title}
        </div>
      )}
      <div className={`SectionContent ${!columnsWidthArray ? `cols-${section.columns || 1}` : ''}`}>
        {section.sectionData?.map((mappedData, i) => {
          const flexWidth = columnsWidthArray
            ? columnsWidthArray[i % columnsWidthArray.length]
            : undefined;

          return (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              className="SectionContent_Item"
              style={flexWidth ? { flex: `${flexWidth} 1 ${flexWidth}%` } : undefined}
            >
              {renderSectionItem(mappedData, i)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomDataReportSection;
